import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ActivityFacts, PeopleBackfill, Person, PersonObservation } from '../api/types.js';
import { useErasePerson, useLinkAccount, useObservationDecisions, usePeople, usePersonHistory, useReadActivity, useLinkProposals, useSeparateAccount, useUpdatePerson } from '../hooks/use-people.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { useRevokeActor } from '../hooks/use-tickets.js';
import { timeAgo } from '../utils/time-ago.js';
import { ALONE_HINT, mergedLabel, topDirs } from '../utils/activity-facts.js';
import { Button } from './button.js';
import { OtherServers } from './person-servers.js';
import { Team } from './people-team.js';

// The people of a server (ADR-030): one row per person, their accounts on each system with the operator's trust on
// each, what they did per repository, and the operator's decisions. Accounts are linked only here, by the operator.

const plural = (n: number, word: string, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** Whether the departure day is past (today included) */
const left = (p: Pick<Person, 'leftOn'>) => !!p.leftOn && p.leftOn <= new Date().toISOString().slice(0, 10);
const day = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function totals(activity: Record<string, ActivityFacts>) {
  const rows = Object.values(activity);
  return {
    repos: rows.length,
    prs: rows.reduce((n, r) => n + r.prs, 0),
    merged: rows.reduce((n, r) => n + r.merged, 0),
    tickets: rows.reduce((n, r) => n + r.tickets, 0),
    comments: rows.reduce((n, r) => n + r.comments, 0),
    last: rows.reduce((l, r) => (r.last > l ? r.last : l), ''),
  };
}

// The same names as LANGUAGES in src/services/languages.ts (API)
const LANGUAGE_NAMES: Record<string, string> = { fr: 'French', en: 'English', nl: 'Dutch', de: 'German', es: 'Spanish', it: 'Italian' };

/** « Writes on Slack in French (34), English (5) », over the person's accounts, most used first; empty when none */
export function languagesSummary(accounts: ReadonlyArray<{ languages?: Record<string, number> }>): string {
  const sum: Record<string, number> = {};
  for (const a of accounts) for (const [l, n] of Object.entries(a.languages ?? {})) if (LANGUAGE_NAMES[l]) sum[l] = (sum[l] ?? 0) + n;
  const list = Object.entries(sum).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  return list.length ? `Writes on Slack in ${list.map(([l, n]) => `${LANGUAGE_NAMES[l]} (${n})`).join(', ')}` : '';
}

/** One sentence on what a person did, the most telling count first */
export function activitySummary(activity: Record<string, ActivityFacts>): string {
  const t = totals(activity);
  if (!t.repos) return 'No activity seen yet';
  const what = t.prs
    ? `${plural(t.prs, 'pull request')}${t.merged ? `, ${t.merged} merged` : ''}`
    : t.merged ? `${plural(t.merged, 'pull request')} merged` : t.tickets ? plural(t.tickets, 'ticket') : plural(t.comments, 'comment');
  return `${what} in ${plural(t.repos, 'repository', 'repositories')}, last active ${timeAgo(t.last)}`;
}

// Marks drawn where they carry information: which system an account is on, whether you trust it. The word stays for
// screen readers. Brand marks from Simple Icons (CC0).
const MARKS: Record<string, string> = {
  github: 'M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.4-1.8-1.4-1.8-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-6 0-1.2.5-2.3 1.3-3.1-.2-.4-.6-1.6 0-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8.1 3.2.8.8 1.2 1.9 1.2 3.1 0 4.6-2.8 5.6-5.5 5.9.5.4.9 1.1.9 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .3',
  gitlab: 'm23.6 9.6-.03-.09-3.27-8.53a.85.85 0 0 0-1.62.06l-2.2 6.75H7.53L5.33 1.04a.85.85 0 0 0-1.62-.06L.43 9.51l-.03.08a6.07 6.07 0 0 0 2 7l.01.01.03.02 4.98 3.73 2.47 1.86 1.5 1.14a1 1 0 0 0 1.22 0l1.5-1.14 2.47-1.86 5.01-3.75.01-.01a6.08 6.08 0 0 0 2-6.99',
  jira: 'M11.57.02H.44a5.22 5.22 0 0 0 5.22 5.22h2.05v1.97a5.22 5.22 0 0 0 5.22 5.22V1.38c0-.75-.6-1.36-1.36-1.36M17.07 5.52H5.95a5.22 5.22 0 0 0 5.22 5.22h2.05v1.98a5.22 5.22 0 0 0 5.22 5.21V6.88c0-.75-.6-1.36-1.37-1.36M22.58 11.03H11.45a5.22 5.22 0 0 0 5.22 5.22h2.05v1.97A5.22 5.22 0 0 0 23.94 23.44V12.39c0-.75-.6-1.36-1.36-1.36',
  slack: 'M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z',
};

/** The mark of a system, by its id (github, gitlab, jira, slack, mail); a plain circle for the others */
function SystemMark({ id }: { id: string }) {
  const path = MARKS[id];
  if (path) return <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="currentColor"><path d={path} /></svg>;
  if (id === 'mail') {
    return <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
  }
  return <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="8" /></svg>;
}

function TrustMark({ operator, trusted }: { operator?: boolean; trusted?: boolean }) {
  if (operator) {
    return (
      <span className="inline-flex items-center" style={{ color: 'var(--color-brand-light)' }} title="Your own account">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
        <span className="sr-only">, you</span>
      </span>
    );
  }
  if (!trusted) return null;
  return (
    <span className="inline-flex items-center" style={{ color: 'var(--color-success)' }} title="You trust this account">
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="m5 12 5 5 9-10" /></svg>
      <span className="sr-only">, trusted</span>
    </span>
  );
}

/** An account as people read it: github:alice → alice on GitHub */
function accountLabel(ref: string, login: string): { name: string; system: string; key: string } {
  const key = ref.split(':', 1)[0];
  const names: Record<string, string> = { github: 'GitHub', gitlab: 'GitLab', jira: 'Jira', mail: 'mail', slack: 'Slack' };
  return { name: login, system: names[key] ?? key, key };
}

function Field({ label, value, placeholder, onSave, saving, type = 'text' }: { label: string; value?: string; placeholder: string; onSave: (v: string) => void; saving: boolean; type?: 'text' | 'date' }) {
  const [draft, setDraft] = useState(value ?? '');
  const changed = draft.trim() !== (value ?? '');
  return (
    <label className="flex flex-col gap-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
      {label}
      <span className="flex gap-2">
        <input
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
        />
        {changed && (
          <Button size="sm" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </span>
    </label>
  );
}

export const KIND: Record<PersonObservation['kind'], string> = { expectation: 'Expects', recurring: 'Comes back to', practice: 'Works this way', 'review-point': 'Reviews point out' };

/** One proposed line: editable, then accepted as it reads, or dropped */
function Proposal({ p, onAccept, onDrop, busy }: { p: PersonObservation; onAccept: (text: string) => void; onDrop: () => void; busy: boolean }) {
  const [text, setText] = useState(p.text);
  return (
    <li className="space-y-1">
      <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{KIND[p.kind]}, seen in {[p.source, ...(p.sources ?? [])].map((s) => s.replace(/^(github|jira):(ticket\/[^/]+\/)?/, '')).join(', ')}</span>
      <span className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} aria-label={`Proposed observation: ${p.text}`}
          className="min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
        <Button size="sm" onClick={() => onAccept(text)} disabled={busy || !text.trim()}>Accept</Button>
        <Button size="sm" variant="ghost" onClick={onDrop} disabled={busy}>Drop</Button>
      </span>
    </li>
  );
}

function Observations({ serverId, person }: { serverId: string; person: Person }) {
  const { accept, drop, remove } = useObservationDecisions(serverId);
  const accepted = person.observations ?? [];
  const proposed = person.proposals ?? [];
  const busy = accept.isPending || drop.isPending || remove.isPending;
  const error = accept.error ?? drop.error ?? remove.error;
  return (
    <div>
      <h3 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Observations</h3>
      {accepted.length ? (
        <ul className="space-y-1">
          {accepted.map((o) => (
            <li key={o.id} className="flex items-baseline gap-2 text-sm">
              <span className="shrink-0 text-xs" style={{ color: 'var(--color-text-3)' }}>{KIND[o.kind]}</span>
              <span className="min-w-0 flex-1" style={{ color: 'var(--color-text)' }}>{o.text}</span>
              <Button size="sm" variant="ghost" onClick={() => remove.mutate({ id: person.id, observationId: o.id })} disabled={busy} aria-label={`Remove: ${o.text}`}>Remove</Button>
            </li>
          ))}
        </ul>
      ) : <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>None accepted yet. Only what you accept here is given to sessions.</p>}
      {proposed.length > 0 && (
        <div className="mt-3">
          <h4 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Seen in two tickets or pull requests, waiting on you</h4>
          <ul className="space-y-2">
            {proposed.map((p) => (
              <Proposal key={p.id} p={p} busy={busy}
                onAccept={(text) => accept.mutate({ id: person.id, proposalId: p.id, text })}
                onDrop={() => drop.mutate({ id: person.id, proposalId: p.id })} />
            ))}
          </ul>
        </div>
      )}
      {(person.waitingProposals ?? 0) > 0 && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
          {person.waitingProposals === 1 ? '1 more line was' : `${person.waitingProposals} more lines were`} seen once: shown when a second ticket or pull request confirms {person.waitingProposals === 1 ? 'it' : 'them'}, gone after 30 days otherwise.
        </p>
      )}
      {error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not saved: {(error as Error).message}</p>}
    </div>
  );
}

function History({ serverId, personId }: { serverId: string; personId: string }) {
  const { data, error } = usePersonHistory(serverId, personId, true);
  if (error) return <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>History not loaded: {(error as Error).message}</p>;
  if (!data) return <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Loading the history…</p>;
  return (
    <ol className="space-y-1">
      {data.map((e) => (
        <li key={`${e.at}-${e.change}`} className="flex flex-wrap gap-x-2 text-xs">
          <span style={{ color: 'var(--color-text-3)' }} title={new Date(e.at).toLocaleString()}>{timeAgo(e.at)}</span>
          <span style={{ color: 'var(--color-text-2)' }}>{e.change}</span>
          {e.source !== 'operator' && <span style={{ color: 'var(--color-text-3)' }}>({e.source.replace(/^(github|jira):(ticket\/[^/]+\/)?/, '')})</span>}
        </li>
      ))}
    </ol>
  );
}

function PersonRow({ serverId, person, others }: { serverId: string; person: Person; others: Person[] }) {
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState('');
  const update = useUpdatePerson(serverId);
  const link = useLinkAccount(serverId);
  const separate = useSeparateAccount(serverId);
  const proposedLinks = useLinkProposals(serverId);
  const links = person.linkProposals ?? [];
  const revoke = useRevokeActor(serverId);
  const erase = useErasePerson(serverId);
  const [confirmErase, setConfirmErase] = useState(false);
  const repos = Object.entries(person.activity).sort((a, b) => b[1].last.localeCompare(a[1].last));
  const gone = left(person);
  const save = (field: 'name' | 'role' | 'growthTarget' | 'leftOn') => (value: string) => update.mutate({ id: person.id, fields: { [field]: value } });
  const saveTeams = (value: string) => update.mutate({ id: person.id, fields: { teams: value.split(',').map((t) => t.trim()).filter(Boolean) } });
  const linkable = others.flatMap((p) => p.facets.map((f) => ({ ref: f.ref, label: `${f.login} on ${accountLabel(f.ref, f.login).system}, now ${p.name}` })));
  const error = update.error ?? link.error ?? separate.error ?? revoke.error ?? erase.error;

  return (
    <li className="border-b py-3" style={{ borderColor: 'var(--color-border)' }} data-testid="person">
      <details open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
        <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm font-medium" style={{ color: gone ? 'var(--color-text-2)' : 'var(--color-text)' }}>{person.name}</span>
          {(person.teams?.length || person.role) && (
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{[person.role, ...(person.teams ?? [])].filter(Boolean).join(', ')}</span>
          )}
          {person.priority && <span className="text-xs" style={{ color: 'var(--color-brand-light)' }}>put first</span>}
          {person.leftOn && (
            <span className="text-xs" style={{ color: gone ? 'var(--color-text-3)' : 'var(--color-warn)' }}>
              {`${gone ? 'left' : 'leaves'} on ${day(person.leftOn)}`}
            </span>
          )}
          <span className="flex flex-wrap gap-1.5">
            {person.facets.map((f) => {
              const a = accountLabel(f.ref, f.login);
              return (
                <span key={f.ref} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text-2)' }}
                  title={f.operator ? `Your own account on ${a.system}` : f.trust ? `You trust ${a.name} on ${a.system}` : `${a.name} on ${a.system}`}>
                  <span style={{ color: 'var(--color-text-3)' }}><SystemMark id={a.key} /></span>
                  {a.name}<span className="sr-only"> on {a.system}</span>
                  <TrustMark operator={f.operator} trusted={!!f.trust} />
                </span>
              );
            })}
          </span>
          {person.linkedServers?.length ? (
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
              {`also on ${person.linkedServers.map((s) => s.serverName).join(', ')}`}
            </span>
          ) : null}
          <span className="w-full text-xs" style={{ color: 'var(--color-text-3)' }}>
            {activitySummary(person.activity)}
            {(person.proposals?.length ?? 0) > 0 && (
              <span style={{ color: 'var(--color-brand-light)' }}>
                {', '}
                <span aria-hidden="true" className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ backgroundColor: 'var(--color-brand-light)' }} />
                {`${person.proposals!.length} ${person.proposals!.length === 1 ? 'observation' : 'observations'} to review`}
              </span>
            )}
            {links.length > 0 && (
              <span style={{ color: 'var(--color-brand-light)' }}>
                {', '}
                <span aria-hidden="true" className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ backgroundColor: 'var(--color-brand-light)' }} />
                {`${links.length} ${links.length === 1 ? 'link' : 'links'} to review`}
              </span>
            )}
          </span>
          {languagesSummary(person.facets) && (
            <span className="w-full text-xs" style={{ color: 'var(--color-text-3)' }}>{languagesSummary(person.facets)}</span>
          )}
        </summary>

        {open && (
          <div className="mt-4 grid gap-6 lg:grid-cols-2">
            <section className="space-y-3">
              <Field label="Name" value={person.name} placeholder="Their name" onSave={save('name')} saving={update.isPending} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Role" value={person.role} placeholder="Developer, lead…" onSave={save('role')} saving={update.isPending} />
                <Field label="Teams" value={person.teams?.join(', ')} placeholder="Their teams, separated by commas" onSave={saveTeams} saving={update.isPending} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Learning" value={person.growthTarget} placeholder="An area to grow into, for Reassign suggestions" onSave={save('growthTarget')} saving={update.isPending} />
                <Field label="Leaves on" type="date" value={person.leftOn} placeholder="" onSave={save('leftOn')} saving={update.isPending} />
              </div>
              {person.leftOn && (
                <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Not suggested for Reassign from 30 days before this day. Clear the date if they stay.</p>
              )}
              <label className="flex items-start gap-2 text-sm max-lg:min-h-10" style={{ color: 'var(--color-text-2)' }}>
                <input type="checkbox" className="mt-1" checked={!!person.priority} disabled={update.isPending}
                  onChange={(e) => update.mutate({ id: person.id, fields: { priority: e.target.checked } })} />
                <span>
                  Put first in To do
                  <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>What they ask you, in Slack, by mail or in Jira, comes before the rest of its kind.</span>
                </span>
              </label>

              <div>
                <h3 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Accounts</h3>
                <ul className="space-y-1">
                  {person.facets.map((f) => {
                    const a = accountLabel(f.ref, f.login);
                    return (
                      <li key={f.ref} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        <span style={{ color: 'var(--color-text)' }}>{a.name}</span>
                        <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
                          {a.system}{f.operator ? ', your own account' : f.trust ? `, trusted ${f.decidedAt ? timeAgo(f.decidedAt) : ''}` : ', not trusted'}
                        </span>
                        <span className="ml-auto flex gap-1">
                          {f.trust && !f.operator && (
                            <Button size="sm" variant="ghost" onClick={() => revoke.mutate(f.ref)} disabled={revoke.isPending} aria-label={`Revoke your trust in ${a.name} on ${a.system}`}>
                              Revoke
                            </Button>
                          )}
                          {person.facets.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => separate.mutate({ id: person.id, ref: f.ref })} disabled={separate.isPending} aria-label={`Separate ${a.name} on ${a.system} into a person of its own`}>
                              Separate
                            </Button>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {links.length > 0 && (
                  <div className="mt-3">
                    <h4 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Proposed links</h4>
                    <ul className="space-y-1.5">
                      {links.map((l) => {
                        const a = accountLabel(l.ref, l.name);
                        return (
                          <li key={l.ref} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                            <span style={{ color: 'var(--color-text)' }}>{`${a.name} on ${a.system}`}</span>
                            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{l.signal}</span>
                            <span className="ml-auto flex gap-1">
                              <Button size="sm" onClick={() => proposedLinks.accept.mutate({ id: person.id, ref: l.ref })} disabled={proposedLinks.accept.isPending} aria-label={`Link ${a.name} on ${a.system} to ${person.name}`}>Link</Button>
                              <Button size="sm" variant="ghost" onClick={() => proposedLinks.drop.mutate({ id: person.id, ref: l.ref })} disabled={proposedLinks.drop.isPending} aria-label={`Drop the proposed link of ${a.name} on ${a.system}`}>Drop</Button>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {linkable.length > 0 && (
                  <div className="mt-2 flex gap-2">
                    <select value={linking} onChange={(e) => setLinking(e.target.value)} aria-label={`An account to link to ${person.name}`}
                      className="min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}>
                      <option value="">Link another account of {person.name}…</option>
                      {linkable.map((l) => <option key={l.ref} value={l.ref}>{l.label}</option>)}
                    </select>
                    <Button size="sm" disabled={!linking || link.isPending} onClick={() => link.mutate({ id: person.id, ref: linking }, { onSuccess: () => setLinking('') })}>
                      {link.isPending ? 'Linking…' : 'Link'}
                    </Button>
                  </div>
                )}
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
                  Your trust stays on each account: linking accounts does not extend it.
                </p>
              </div>
              {error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not saved: {(error as Error).message}</p>}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
                <Link to={`/servers/${serverId}/people/${person.id}/sheet`} className="text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>
                  One-page sheet for a one-to-one
                </Link>
                <Button size="sm" variant="ghost" onClick={() => setConfirmErase(true)} disabled={erase.isPending} style={{ color: 'var(--color-error)' }}>
                  {erase.isPending ? 'Erasing…' : 'Erase this person'}
                </Button>
              </div>
              <ConfirmDialog
                open={confirmErase}
                title={`Erase ${person.name}`}
                message="Their history, activity and the details you entered go. An account you trust keeps only that decision. New activity from them will start a new profile."
                confirmLabel="Erase"
                onConfirm={() => { setConfirmErase(false); erase.mutate(person.id); }}
                onCancel={() => setConfirmErase(false)}
              />
            </section>

            <section className="space-y-4">
              <div>
                <h3 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Activity</h3>
                {repos.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs" style={{ color: 'var(--color-text-2)' }}>
                      <thead style={{ color: 'var(--color-text-3)' }}>
                        <tr><th className="py-1 pr-3 font-normal">Repository</th><th className="pr-3 font-normal">PRs</th><th className="pr-3 font-normal" title={ALONE_HINT}>Merged</th><th className="pr-3 font-normal">Reviewed</th><th className="pr-3 font-normal">Tickets</th><th className="pr-3 font-normal">Comments</th><th className="font-normal">Last</th></tr>
                      </thead>
                      <tbody>
                        {repos.map(([repo, f]) => (
                          <tr key={repo} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                            <td className="py-1 pr-3 break-all">
                              <span style={{ color: 'var(--color-text)' }}>{repo}</span>
                              {f.dirs && <span className="block" style={{ color: 'var(--color-text-3)' }}>{topDirs(f)}</span>}
                            </td>
                            <td className="pr-3">{f.prs}</td><td className="pr-3 whitespace-nowrap" title={ALONE_HINT}>{mergedLabel(f)}</td><td className="pr-3">{f.reviews ?? 0}</td>
                            <td className="pr-3">{f.tickets}</td><td className="pr-3">{f.comments}</td>
                            <td title={new Date(f.last).toLocaleString()}>{timeAgo(f.last)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Nothing seen yet: activity counts from now on, from the GitHub events of this server.</p>}
              </div>
              <Observations serverId={serverId} person={person} />
              <OtherServers serverId={serverId} person={person} />
              <div>
                <h3 className="mb-1 text-xs" style={{ color: 'var(--color-text-3)' }}>History</h3>
                <History serverId={serverId} personId={person.id} />
              </div>
            </section>
          </div>
        )}
      </details>
    </li>
  );
}

/** Reading the last 6 months from GitHub: the button, then where the last reading stands */
function ReadActivity({ serverId, backfill }: { serverId: string; backfill?: PeopleBackfill }) {
  const read = useReadActivity(serverId);
  const running = read.isPending || backfill?.status === 'running';
  const state = !backfill ? null
    : backfill.status === 'running' ? 'Reading GitHub…'
      : backfill.status === 'done' ? `Read ${timeAgo(backfill.finishedAt ?? backfill.startedAt)}: ${backfill.repos ?? 0} ${backfill.repos === 1 ? 'repository' : 'repositories'}, ${backfill.accounts ?? 0} ${backfill.accounts === 1 ? 'account' : 'accounts'}`
        : `The last reading failed: ${backfill.error ?? 'unknown reason'}`;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button size="sm" variant="ghost" onClick={() => read.mutate()} disabled={running}>
        {running ? 'Reading GitHub…' : backfill?.status === 'done' ? 'Read GitHub again' : 'Read the last 6 months from GitHub'}
      </Button>
      {state && !running && (
        <span className="text-xs" style={{ color: backfill?.status === 'failed' ? 'var(--color-error)' : 'var(--color-text-3)' }} data-testid="backfill-state">{state}</span>
      )}
      {read.error && <span role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not started: {(read.error as Error).message}</span>}
    </div>
  );
}

/** People one by one, or the team per repository */
export function People({ serverId }: { serverId: string }) {
  const [view, setView] = useState<'people' | 'team'>('people');
  return (
    <div>
      <div role="group" aria-label="View" className="mb-4 inline-flex rounded-md border p-0.5" style={{ borderColor: 'var(--color-border)' }}>
        {(['people', 'team'] as const).map((v) => (
          <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
            className="rounded px-3 py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-1"
            style={view === v ? { backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)' } : { color: 'var(--color-text-3)' }}>
            {v === 'people' ? 'People' : 'Team'}
          </button>
        ))}
      </div>
      {view === 'people' ? <PeopleList serverId={serverId} /> : <Team serverId={serverId} />}
    </div>
  );
}

function PeopleList({ serverId }: { serverId: string }) {
  const { data: raw, error, isLoading } = usePeople(serverId);
  const data = raw?.people;
  const people = useMemo(() => (data ?? [])
    .map((p) => ({ p, last: totals(p.activity).last, gone: left(p) }))
    // Those who left go last
    .sort((a, b) => Number(a.gone) - Number(b.gone) || b.last.localeCompare(a.last) || a.p.name.localeCompare(b.p.name))
    .map(({ p }) => p), [data]);
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Loading the people of this server…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not load them: {(error as Error).message}</p>;
  return (
    <div>
      <p className="mb-4 max-w-[72ch] text-sm" style={{ color: 'var(--color-text-3)' }}>
        The people you work with on this server, most recently active first, those who left at the end. They appear as they open tickets, comment
        or open pull requests. Link the accounts that belong to the same person; your trust stays on each account.
      </p>
      {/* GitHub only: a server whose code is elsewhere has nothing to read there */}
      {raw?.github !== false && <ReadActivity serverId={serverId} backfill={raw?.backfill} />}
      {people.length ? (
        <ul>{people.map((p) => <PersonRow key={p.id} serverId={serverId} person={p} others={people.filter((o) => o.id !== p.id)} />)}</ul>
      ) : (
        <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>No one yet. People appear here as they open tickets, comment or open pull requests on this server&apos;s repositories.</p>
      )}
    </div>
  );
}
